import { Injectable } from '@angular/core';
import { PendingSkuInterface, PricingDetails, PricingDetailsModel, PricingModel } from '@app/modules/tf-pricing';
import { FishersciPricingInterface } from '@app/modules/tf-pricing/model/interfaces/fishersci-pricing.interface';
import { FishersciPricingApiService } from '@app/modules/tf-pricing/services/fishersci-pricing-api.service';
import { ProductSiteRegionEnum } from '@app/models/enums/product-site-region.enum';
import { ProductSiteRegionService } from '@app/services/product-site-region.service';
import { map, Observable, of, Subject } from 'rxjs';

export interface FishersciPricingSku {
    catalogNumber: string;
    fisherPartNumber?: string;
}

@Injectable()
export class FishersciPricingService {

    serial: number = 0;

    pendingObservables: Record<number, PendingSkuInterface> = {};
    pricingDetailsRecord: Record<string, PricingModel> = {};

    private readonly countryCurrencyCodes: Record<string, string> = {
        AT: 'EUR',
        BE: 'EUR',
        CA: 'CAD',
        CH: 'CHF',
        DE: 'EUR',
        DK: 'DKK',
        ES: 'EUR',
        FI: 'EUR',
        FR: 'EUR',
        GB: 'GBP',
        IE: 'EUR',
        IT: 'EUR',
        NL: 'EUR',
        NO: 'NOK',
        PT: 'EUR',
        SE: 'SEK',
        US: 'USD'
    };

    constructor(
        private fishersciPricingApiService: FishersciPricingApiService,
        private productSiteRegionService: ProductSiteRegionService
    ) {
    }

    preloadPricingInfo(skus: Array<string | FishersciPricingSku>): void {

        const uniqueSkus = this.filterUniqueSkus(skus);

        for (const skuData of uniqueSkus) {
            const catalogNumber = this.getCatalogNumber(skuData);
            const fisherPartNumber = this.getFisherPartNumber(skuData);

            this.fishersciPricingApiService.getPrice(catalogNumber, fisherPartNumber).subscribe({
                next: (data) => {
                    this.processObservables(catalogNumber, data);
                },
                error: (error) => {
                    console.log(error);
                }
            })

        }

    }


    processObservables(sku: string, data: FishersciPricingInterface): void {

        const pricingDetail = this.processPricingDetails(data);

        this.pricingDetailsRecord[sku] = new PricingModel({
            catalogNumber: sku,
            pricingDetails: pricingDetail,
            pricingServiceError: pricingDetail.error
        });

        Object.entries(this.pendingObservables).forEach(([serial, pendingObservable]) => {
            if (pendingObservable.sku === sku) {
                pendingObservable.subject.next(this.pricingDetailsRecord[pendingObservable.sku]);
                pendingObservable.subject.complete();
                delete this.pendingObservables[serial];
            }
        });

    }

    processPricingDetails(pricingData: FishersciPricingInterface): PricingDetailsModel {

        const priceDetails = this.getPriceDetails(pricingData);

        let pricingDetailsModel = new PricingDetailsModel();

        if (priceDetails) {

            // TODO: We may need to map more fields here
            const details: PricingDetails ={
                finalPrice: this.formatItemPrice(priceDetails.itemPrice),
                fisherPartNumber: pricingData.fisherPartNumber,
                pricingCurrencyCode: this.getPricingCurrencyCode(priceDetails.itemPrice, pricingData)
            };

            pricingDetailsModel = new PricingDetailsModel(details);

            return pricingDetailsModel;

        }

        return pricingDetailsModel;

    }

    private getPriceDetails(pricingData: FishersciPricingInterface): { itemPrice: string; uom: string; quantity: string } {
        if (pricingData
            && Array.isArray(pricingData.priceUoms)
            && pricingData.priceUoms.length > 0
        ) {
            return pricingData.priceUoms[0];
        }

        if (pricingData
            && Array.isArray(pricingData.priceComs)
            && pricingData.priceComs.length > 0
        ) {
            return pricingData.priceComs[0];
        }

        return null;
    }

    private formatItemPrice(itemPrice: string): string {
        return itemPrice
            ?.replace(/&nbsp;|&#160;/gi, ' ')
            .replace(/&euro;|&#8364;/gi, '\u20ac')
            .replace(/\s+/g, ' ')
            .trim();
    }

    private getPricingCurrencyCode(itemPrice: string, pricingData?: FishersciPricingInterface): string {
        const countryCode = this.getCountryCodeFromLocale(pricingData?.locale) || this.getCountryCodeFromRegion();

        if (countryCode && this.countryCurrencyCodes[countryCode]) {
            return this.countryCurrencyCodes[countryCode];
        }

        return this.getCurrencyCodeFromPrice(itemPrice);
    }

    private getCountryCodeFromLocale(locale?: string): string {
        return locale?.split(/[-_]/).pop()?.toUpperCase() || '';
    }

    private getCountryCodeFromRegion(): string {
        const productSiteRegion = this.productSiteRegionService.getProductSiteRegion();

        if (ProductSiteRegionEnum.FISHERSCI_US === productSiteRegion) {
            return 'US';
        }

        if (ProductSiteRegionEnum.FISHERSCI_CA === productSiteRegion) {
            return 'CA';
        }

        if (ProductSiteRegionEnum.FISHERSCI_EU === productSiteRegion) {
            return this.productSiteRegionService.getFisherSciEuropeanCountryCode() || '';
        }

        return '';
    }

    private getCurrencyCodeFromPrice(itemPrice: string): string {
        const price = itemPrice?.toLowerCase() || '';

        if (price.match(/&euro;|&#8364;|\u20ac/)) {
            return 'EUR';
        }

        if (price.match(/&pound;|&#163;|\u00a3/)) {
            return 'GBP';
        }

        if (price.match(/\bchf\b/)) {
            return 'CHF';
        }

        return '';
    }

    getPreloadedPricing(sku: string): Observable<PricingModel> {

        if (this.pricingDetailsRecord[sku]) {
            return of(this.pricingDetailsRecord[sku]);
        }

        const data: PendingSkuInterface = {
            sku: sku,
            subject: new Subject<PricingModel>()
        };

        this.pendingObservables[this.serial++] = data;

        return data.subject;

    }

    getPricing(sku: string, fisherPartNumber?: string): Observable<PricingModel> {

        if (this.pricingDetailsRecord[sku]) {
            return of(this.pricingDetailsRecord[sku]);
        }

        return this.retrievePricingInfo(sku, fisherPartNumber).pipe(
            map(() => {
                return this.pricingDetailsRecord[sku];
            })
        );

    }

    retrievePricingInfo(sku: string, fisherPartNumber?: string): Observable<PricingModel> {

        const data: PendingSkuInterface = {
            sku: sku,
            subject: new Subject<PricingModel>()
        };

        this.fishersciPricingApiService.getPrice(sku, fisherPartNumber).subscribe({
            next: (data) => {
                this.processObservables(sku, data);
            },
            error: (error) => {
                console.log(error);
            }
        })

        this.pendingObservables[this.serial++] = data;

        return data.subject;

    }

    private filterUniqueSkus(skus: Array<string | FishersciPricingSku>): Array<string | FishersciPricingSku> {
        const seenCatalogNumbers: string[] = [];

        return skus.filter((item) => {
            const catalogNumber = this.getCatalogNumber(item);

            if (seenCatalogNumbers.includes(catalogNumber)) {
                return false;
            }

            seenCatalogNumbers.push(catalogNumber);
            return true;
        });
    }

    private getCatalogNumber(sku: string | FishersciPricingSku): string {
        return typeof sku === 'string' ? sku : sku.catalogNumber;
    }

    private getFisherPartNumber(sku: string | FishersciPricingSku): string {
        return typeof sku === 'string' ? '' : sku.fisherPartNumber || '';
    }

    getAnonymousCurrencyCode(): string {

        let currencyCode = null;

        for (const sku in this.pricingDetailsRecord) {

            if (this.pricingDetailsRecord[sku]?.pricingDetails?.pricingCurrencyCode != undefined) {
                currencyCode = this.pricingDetailsRecord[sku]?.pricingDetails?.pricingCurrencyCode;
                break;
            }

        }

        return currencyCode != null ? currencyCode : '';

    }

}
